import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Check, X, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, isPremium } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/membership/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      toast.error("Bitte melden Sie sich zuerst an.");
      navigate("/login?redirect=/membership");
      return;
    }

    if (planId === "free") {
      toast.info("Sie haben bereits den kostenlosen Plan.");
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.");
      console.error("Payment error:", error);
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const freePlan = plans.find((p) => p.id === "free");
  const premiumPlan = plans.find((p) => p.id === "premium");

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="membership-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 font-['Chivo']">
            Werden Sie <span className="text-cyan-400">Mitglied</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Profitieren Sie von exklusiven Vorteilen und sparen Sie bei jeder Buchung.
            Wählen Sie den Plan, der am besten zu Ihnen passt.
          </p>
        </div>

        {/* Plans Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Free Plan */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8" data-testid="free-plan-card">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-2">
                {freePlan?.name_de || "Basis-Mitglied"}
              </h3>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-white price-tag">0€</span>
                <span className="text-zinc-500 ml-2">/ kostenlos</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {(freePlan?.features_de || []).map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-zinc-400">{feature}</span>
                </li>
              ))}
              <li className="flex items-start gap-3">
                <X className="h-5 w-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Kein Rabatt auf Services</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="h-5 w-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">Keine exklusiven Angebote</span>
              </li>
            </ul>

            {isAuthenticated ? (
              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-400"
                disabled
              >
                Aktueller Plan
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full border-zinc-700 text-white hover:bg-zinc-800"
                onClick={() => navigate("/register")}
                data-testid="free-register-btn"
              >
                Kostenlos registrieren
              </Button>
            )}
          </div>

          {/* Premium Plan */}
          <div className="membership-premium bg-zinc-900 rounded-sm p-8 relative" data-testid="premium-plan-card">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="premium-badge text-black">EMPFOHLEN</span>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-5 w-5 text-cyan-400" />
                <h3 className="text-xl font-semibold text-white">
                  {premiumPlan?.name_de || "Premium-Mitglied"}
                </h3>
              </div>
              <div className="flex items-baseline">
                <span className="text-4xl font-bold text-white price-tag">
                  {premiumPlan?.price?.toFixed(2) || "19,99"}€
                </span>
                <span className="text-zinc-500 ml-2">/ Monat</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {(premiumPlan?.features_de || []).map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-zinc-300">{feature}</span>
                </li>
              ))}
            </ul>

            {isPremium ? (
              <Button className="w-full bg-zinc-700 text-zinc-400" disabled>
                Bereits Premium-Mitglied
              </Button>
            ) : (
              <Button
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold btn-glow"
                onClick={() => handleUpgrade("premium")}
                disabled={processingPayment}
                data-testid="upgrade-premium-btn"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird verarbeitet...
                  </>
                ) : (
                  <>
                    Jetzt upgraden
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8">
          <h2 className="text-xl font-semibold text-white mb-6 font-['Chivo']">
            Häufig gestellte Fragen
          </h2>
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-white mb-2">
                Kann ich jederzeit kündigen?
              </h4>
              <p className="text-sm text-zinc-500">
                Ja, die Premium-Mitgliedschaft kann jederzeit zum Monatsende gekündigt werden.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-white mb-2">
                Gilt der Rabatt für alle Services?
              </h4>
              <p className="text-sm text-zinc-500">
                Ja, als Premium-Mitglied erhalten Sie 15% Rabatt auf alle unsere Aufbereitungsservices.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-white mb-2">
                Wie funktioniert die kostenlose monatliche Auffrischung?
              </h4>
              <p className="text-sm text-zinc-500">
                Premium-Mitglieder können einmal pro Monat eine kostenlose Innenraumauffrischung in Anspruch nehmen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
